import { AggPageComponent, FormMode } from 'common/constants/enums';
import { PagesContext } from 'context/pages-context';
import { useContext, useEffect, useState } from 'react';
import ReactCountryFlag from 'react-country-flag';

import { IFetchBestPagesOptions } from '../../../../common/interfaces/pages-api';
import { FormItem } from '../../../../common/ui/formItem';
import { Select } from '../../../../common/ui/select';
import { AggPageFormContext } from '../../../../context/page-form-context';
import { TSetFilter, useActions, useAppSelector } from '../../../../hooks';

interface PublisherSelectOption {
  label: string;
  value: number;
  country?: string;
}

interface IPublisherSelectProps {
  formMode: string;
  updateFilter?: TSetFilter<Omit<IFetchBestPagesOptions, 'attribution'>>;
  onPublisherChange?: (data: { id: number; country?: string }) => void;
  label?: string;
  placeholder?: string;
  className?: string
}

const getCountryCode = (country?: string): string => {
  if (!country) return 'US';
  // Convert lowercase ISO 2-letter code to uppercase, handle 'uk' -> 'GB' mapping
  return country.toLowerCase() === 'uk' ? 'GB' : country.toUpperCase();
};

const formatPublisherOptionLabel = (option: PublisherSelectOption) => (
  <div style={{ alignItems: 'center', display: 'flex', gap: '8px' }}>
    <ReactCountryFlag
      countryCode={getCountryCode(option.country)}
      style={{ fontSize: '1.2em' }}
      svg
    />
    <span>{option.label}</span>
  </div>
);

export const PublisherSelect = ({
  className = '',
  formMode,
  label,
  onPublisherChange,
  placeholder,
  updateFilter,
}: IPublisherSelectProps) => {
  const { changePublisher, error, publisher } = useContext(PagesContext);
  const { changeDomainId } = useContext(AggPageFormContext);
  const { loading, publishers, ...publisherListData } = useAppSelector(
    ({ pages }) => pages.publisherList,
  );

  const { fetchPublisherListRequest } = useActions();
  const [selectOptions, setSelectOptions] = useState<PublisherSelectOption[]>([
    {
      country: 'us',
      label: 'ProductPicks',
      value: 0,
    },
  ]);

  const disabled = formMode === FormMode.UPDATE;
  const defaultValue = selectOptions.find((e) => e.value === 0);
  const value = selectOptions.find((e) => e.value === publisher.id);

  const handleChange = (selectedValue: string) => {
    const item = publishers.find((elem) => elem?.id === Number(selectedValue));
    const selectedCountry = item?.country || 'us';

    changePublisher(item || { country: 'us' });

    if (publisher?.id !== 0) {
      changeDomainId(null);
    }

    if (onPublisherChange) {
      onPublisherChange({
        country: selectedCountry,
        id: Number(selectedValue) || 0,
      });
    }

    if (updateFilter) {
      const publisherField = selectedValue === 'ProductPicks' ? null : Number(selectedValue);
      const updateData: Partial<Omit<IFetchBestPagesOptions, 'attribution'>> = { publisher: publisherField };

      if (selectedValue !== 'ProductPicks') {
        updateData.domain = null;
      }

      updateFilter(updateData);
    }
  };

  useEffect(() => {
    setSelectOptions([
      {
        country: 'us',
        label: 'ProductPicks',
        value: 0,
      },
      ...publishers.map((pub) => ({
        country: pub.country || 'us',
        label: pub.display_name || pub.name,
        value: pub.id,
      })),
    ]);
  }, [publishers]);

  useEffect(() => {
    fetchPublisherListRequest();
  }, []);

  return (
    <FormItem
      className={className}
      label={label}
      message={error?.[AggPageComponent.Publisher] || publisherListData.error?.message}
    >
      <Select
        defaultValue={defaultValue}
        formatOptionLabel={formatPublisherOptionLabel}
        isDisabled={disabled}
        isLoading={loading}
        isSearchable={false}
        onChange={(option) => handleChange(option.value.toString())}
        options={selectOptions}
        placeholder={placeholder}
        value={value}
      />
    </FormItem>
  );
};
